import React from "react";
import { type NextPage } from "next";
import { getLandingPageProps, getPageMetaImage, getSafeCustomer, type LandingPageProps } from "utils";
import { Section, sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { Customers } from "components/logos";
import {
  CustomerStats,
  CustomerWithStatsSlug,
  DestinationMarquee,
  IndustryLeaderAwards,
  ProductDemo,
  TraditionalCdpVsHightouch,
} from "components/content";
import {
  implementationEffortComparison,
  sourcesOfTruthComparison,
} from "components/content/traditional-cdp-vs-hightouch/data";
import { CenteredQuote } from "components/quotes";
import { FillImage } from "components/images";
import { SolidPlate } from "ui/plates";
import { Diptych } from "ui/diptych";
import { Button } from "ui/button";
import { SaasDiagram, SaasHero } from "components/solutions/saas";
import productImage from "../../../public/assets/solutions/saas/demo-embed.png";
import mobileProductImage from "../../../public/assets/solutions/saas/demo-embed-mobile.png";
import firstDiptychImage from "../../../public/assets/solutions/saas/diptych-1.png";
import secondDiptychImage from "../../../public/assets/solutions/saas/diptych-2.png";

const SaaS: NextPage<LandingPageProps> = ({ page, announcementBanner, destinations, customers }) => {
  const calendly = getSafeCustomer(customers, "calendly");

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.blue.150"
      footerOptions={{ variant: "blue" }}
    >
      <SaasHero eyebrow={page.eyebrow} title={page.title} description={page.description} />

      {page.customers && (
        <Customers
          title="Trusted by leading B2B Saas companies"
          customers={page.customers}
          my={0}
          mt={{ base: "big", md: 0 }}
        />
      )}

      <CustomerStats
        title="Grow your pipeline faster by putting your data to work"
        customers={customers}
        my={0}
        py={sectionSpacingY}
      />

      <SolidPlate theme="dark" bgColor="new.blue.950" spotlightColor="new.blue.900">
        <SaasDiagram />

        <CenteredQuote
          maxW="960px"
          my={sectionSpacingY}
          theme="dark"
          quoteText="We know so much about our users and have so many signals to build great audiences. With Customer Studio, our marketers can build hyper-specific audiences in minutes, leveraging all of the data in our warehouse, without needing our data team."
          author={{ name: "Darren Chait", job: "Head of Growth Marketing" }}
          company={{ name: "Calendly", logo: calendly.logoMonoUrl }}
          cta={{ text: "Read the story", href: "/customers/calendly" }}
        />

        <ProductDemo
          theme="dark"
          showcaseImageVariant="coastal"
          demoEmbedText="Check out this 5 minute interactive demo to learn how you can sync customer data to any business tool."
          demoEmbedCtaText="Start exploring"
          demoLinkText="Talk to a product expert to learn how you can sync customer data to any business tool."
          productImage={productImage}
          mobileProductImage={mobileProductImage}
        />
      </SolidPlate>

      <TraditionalCdpVsHightouch
        my={0}
        pt={sectionSpacingY}
        title="The Best CDP for B2B"
        titleMaxW="760px"
        description="Don’t put yourself through a long and painful implementation with a traditional CDP, just to fail months later. Traditional CDPs use one-size-fits-all data models that better serve B2C companies. Get started with your existing data today."
        rows={[
          {
            category: "B2B data models",
            traditionalCdp: {
              title: "Rigid data models not built for B2B",
              description:
                "Traditional CDPs only allow you to access and activate basic customer data like users and events.",
            },
            hightouch: {
              title: "Flexible data models that work for B2B",
              description:
                "Access and activate any data that matters to your organization, like accounts, workspaces, opportunities, leads, and data science models.",
            },
          },
          sourcesOfTruthComparison,
          implementationEffortComparison,
        ]}
      />

      <Section>
        <Diptych
          direction="right"
          size="lg"
          title="Empower sales and success teams with a complete view of users, accounts, and product data"
          description="Bring the data that matters to the places your team already lives – whether that’s a CRM like Salesforce or Hubspot, a CS platform like Gainsight, a feed in Slack, or even Airtable."
          actions={
            <Button hasRightChevron href="/integrations">
              See all integrations
            </Button>
          }
          media={
            <FillImage
              src={firstDiptychImage}
              alt="Abstract illustration of a data warehouse and Hightouch integration logos like Salesforce, HubSpot, Gainsight, Slack, and Airtable."
            />
          }
        />

        <Diptych
          mt={sectionSpacingY}
          direction="left"
          size="lg"
          title="Move faster and unlock product-led growth with all of your data"
          description="There are hundreds of ways you can use your data to improve your operations and grow your business – some as simple as sending Slack messages to team members when key events happen. Take some inspiration from our favorite use cases."
          actions={
            <Button hasRightChevron href="/playbooks">
              Explore B2B use cases
            </Button>
          }
          media={
            <FillImage
              src={secondDiptychImage}
              alt="Abstract illustration of data moving through a messaging tool, to Hightouch which connects it to a CRM, and it ends up at a targeted user."
            />
          }
        />
      </Section>

      <IndustryLeaderAwards />

      <DestinationMarquee destinations={destinations} my={0} py={sectionSpacingY} />
    </Page>
  );
};

export const getStaticProps = getLandingPageProps("/solutions/saas", {
  loadDestinations: "all",
  loadCustomers: [CustomerWithStatsSlug.CALENDLY, CustomerWithStatsSlug.RAMP, CustomerWithStatsSlug.GORGIAS],
});

export default SaaS;
